import { useCallback, useRef, useState } from 'react';

import { burracoIdempotencyKey, guastoScritturaOf, teniamoLaChiave, type GuastoScrittura } from './scritture';

/** `rispostaStrana`: un 2xx che non ha la forma attesa (la scrittura puo essere andata: se ne parla senza ritentare da soli). */
export type GuastoAzione = GuastoScrittura | 'rispostaStrana';

export interface StatoAzione {
  /** `true` mentre la richiesta e in volo: i bottoni si spengono (e la guardia sta a parte, in un ref). */
  invio: boolean;
  guasto: GuastoAzione | null;
}

/**
 * UNA scrittura del Burraco dalla Hub (PR2): un'azione dell'utente = una chiave di idempotenza + una sola richiesta in volo.
 *
 * 🔴 **Il doppio tocco non crea due tavoli**, in due modi indipendenti: (1) `inVolo` e un REF, scritto in modo sincrono — il bottone spento e comodita, non la guardia (lo stato React
 * si aggiorna dopo il tocco, e due tocchi nello stesso tick passerebbero entrambi); (2) la chiave di idempotenza (`idempotencyKey` nel corpo), che il game host lega alla persona (K4):
 * una ripetizione della STESSA azione dopo una risposta persa ritrova lo stesso tavolo.
 *
 * COME SI SCEGLIE LA CHIAVE: una nuova alla prima richiesta di un'azione, e la STESSA a ogni ripetizione finche l'esito e incerto (rete caduta, 5xx, 424: la richiesta puo essere arrivata);
 * si scarta dopo un successo e dopo un rifiuto definitivo (un 4xx diverso da 424, `teniamoLaChiave`): la prossima azione e un'altra. ⚠️ La chiave NON e per `tipo`: chi tocca «Gioca» e poi
 * «Invita» dopo un guasto incerto sta facendo due azioni diverse; per questo ogni azione ha il SUO hook (e la sua chiave).
 *
 * `chiamata` riceve la chiave e rende l'esito gia letto (`null` = risposta senza la forma attesa). Rende l'esito, o `null` se non e andata (il guasto sta in `stato`).
 */
export function useAzioneBurraco<R>() {
  const chiave = useRef<string | null>(null);
  const inVolo = useRef(false);
  const [stato, setStato] = useState<StatoAzione>({ invio: false, guasto: null });

  const esegui = useCallback(async (chiamata: (idempotencyKey: string) => Promise<R | null>): Promise<R | null> => {
    if (inVolo.current) return null; // il doppio tocco: la prima richiesta e ancora in volo
    inVolo.current = true;
    setStato({ invio: true, guasto: null });
    chiave.current ??= burracoIdempotencyKey();
    try {
      const esito = await chiamata(chiave.current);
      if (esito === null) {
        // 2xx senza la forma attesa: la scrittura puo essere andata, quindi la chiave RESTA (un nuovo tocco ritrova lo stesso tavolo)
        setStato({ invio: false, guasto: 'rispostaStrana' });
        return null;
      }
      chiave.current = null; // fatto: la prossima azione e un'altra
      setStato({ invio: false, guasto: null });
      return esito;
    } catch (e) {
      if (!teniamoLaChiave(e)) chiave.current = null;
      setStato({ invio: false, guasto: guastoScritturaOf(e) });
      return null;
    } finally {
      inVolo.current = false;
    }
  }, []);

  const azzera = useCallback(() => setStato({ invio: false, guasto: null }), []);
  return { stato, esegui, azzera };
}
